import { memo, useCallback, useMemo } from 'react';
import { Badge, DatatableCell } from '@monksflow/monks-ui';
import { Table, TableRow } from '@/shared/ui';
import type { Column, FilterConfig, SortDirection } from '@/shared/ui';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { formatCategoryLabel, getCategoryBadgeClass } from '@/features/guidelines/constants';
import { GuidelineActionMenu } from '@/features/guidelines/components';
import { ExpandableDescription } from '../ExpandableDescription';
import type { GuidelineResponseDto } from '@/lib/api/guidelines-client';
import { CATEGORIES, type Category } from '@inference.engine/guidelines/web';
import { useTranslation } from 'react-i18next';

type SortableGuidelineKeys = 'name' | 'description' | 'category' | 'created';

interface CategoryFilters {
  category: Category[];
}

const categoryNames = Object.keys(CATEGORIES) as Category[];

interface GuidelineRowProps {
  row: GuidelineResponseDto;
  onEdit?: (guideline: GuidelineResponseDto) => void;
  onDelete?: (guideline: GuidelineResponseDto) => void;
}

// Module-scope: used inside `renderBodyRow`, closes over only the 2
// values threaded as props.
function GuidelineRow({ row, onEdit, onDelete }: GuidelineRowProps) {
  return (
    <TableRow key={row.id} hover className="group cursor-pointer [&>*]:align-top">
      <DatatableCell className="text-sm whitespace-normal wrap-break-word">
        {row.name}
      </DatatableCell>
      <DatatableCell className="overflow-hidden font-normal whitespace-normal">
        <ExpandableDescription text={row.description} />
      </DatatableCell>
      <DatatableCell>
        <Badge roundness="full" className={getCategoryBadgeClass(row.category)}>
          {formatCategoryLabel(row.category)}
        </Badge>
      </DatatableCell>
      <DatatableCell className="text-sm font-normal">
        {formatRelativeDate(row.created)}
      </DatatableCell>
      <DatatableCell>
        <GuidelineActionMenu guideline={row} onEdit={onEdit} onDelete={onDelete} />
      </DatatableCell>
    </TableRow>
  );
}

interface GuidelinesTableProps {
  guidelines: GuidelineResponseDto[];
  isLoading?: boolean;
  sortBy?: SortableGuidelineKeys;
  sortDirection?: SortDirection;
  selectedCategories?: Category[];
  onSort?: (key: SortableGuidelineKeys, direction: SortDirection) => void;
  onCategoryFilterChange?: (categories: Category[]) => void;
  onEdit?: (guideline: GuidelineResponseDto) => void;
  onDelete?: (guideline: GuidelineResponseDto) => void;
}

export const GuidelinesTable = memo(
  ({
    guidelines,
    isLoading = false,
    sortBy = 'created',
    sortDirection = 'desc',
    selectedCategories = [],
    onSort,
    onCategoryFilterChange,
    onEdit,
    onDelete,
  }: GuidelinesTableProps) => {
    const { t } = useTranslation('knowledge');

    const columns = useMemo<Column<GuidelineResponseDto, SortableGuidelineKeys>[]>(
      () => [
        {
          key: 'name',
          label: t('guidelines.guidelinesTable.name'),
          sortable: true,
          className: 'w-[21%]',
        },
        {
          key: 'description',
          label: t('guidelines.guidelinesTable.description'),
          sortable: true,
          className: 'w-[42%]',
        },
        {
          key: 'category',
          label: t('guidelines.guidelinesTable.category'),
          sortable: true,
          filterable: 'multiple',
          className: 'w-[15%]',
        },
        {
          key: 'created',
          label: t('guidelines.guidelinesTable.addedAt'),
          sortable: true,
          className: 'w-[21%]',
        },
        { key: 'id', label: '', sortable: false, className: 'w-12' },
      ],
      [t],
    );

    const filterConfigs = useMemo<Record<string, FilterConfig<CategoryFilters>>>(
      () => ({
        category: {
          key: 'category',
          options: categoryNames.map((category) => ({
            value: category,
            label: formatCategoryLabel(category),
          })),
        },
      }),
      [],
    );

    const filters = useMemo<CategoryFilters>(
      () => ({ category: selectedCategories }),
      [selectedCategories],
    );

    const handleSort = useCallback(
      (property: SortableGuidelineKeys) => {
        // eslint-disable-next-line i18next/no-literal-string -- sort direction state values
        const newDirection = sortBy === property && sortDirection === 'asc' ? 'desc' : 'asc';
        onSort?.(property, newDirection);
      },
      [onSort, sortBy, sortDirection],
    );

    const handleFiltersChange = useCallback(
      (next: CategoryFilters) => {
        onCategoryFilterChange?.(next.category);
      },
      [onCategoryFilterChange],
    );

    return (
      <Table<GuidelineResponseDto, SortableGuidelineKeys, CategoryFilters>
        isLoading={isLoading}
        columns={columns}
        rows={guidelines}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        containerClassName="px-0 pb-0"
        filtering={{ filters, filterConfigs, onFiltersChange: handleFiltersChange }}
        renderBodyRow={(row) => (
          <GuidelineRow key={row.id} row={row} onEdit={onEdit} onDelete={onDelete} />
        )}
      />
    );
  },
);

GuidelinesTable.displayName = 'GuidelinesTable';
